// MACHINERY V2 (Stroje) — fleet card atoms (M2.2).
//
// The small, dumb pieces FleetCard composes: glyphs, the KV cell from the
// artboard's identity grid, the photo well, the health dot and the NEW badge.
// Split out of FleetCard.tsx purely to keep both files under the 300-line cap;
// none of them decides anything, and none of them touches telemetry.
//
// Reference impl: design_handoff_machinery_v3/machinery-v1-big.jsx (KV) +
// machinery-shared.jsx (MachineImage, PinGlyph).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";

export const PinGlyph: React.FC = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
    <path
      d="M5.5 1.2c-1.7 0-3 1.3-3 3 0 2 3 5.6 3 5.6s3-3.6 3-5.6c0-1.7-1.3-3-3-3z"
      stroke="currentColor"
    />
    <circle cx="5.5" cy="4.3" r="1.1" stroke="currentColor" />
  </svg>
);

const CameraGlyph: React.FC = () => (
  <svg
    width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.4" aria-hidden
  >
    <path d="M3 8.5h3.5L8 6.5h8L17.5 8.5H21v11H3z" />
    <circle cx="12" cy="13.5" r="3.4" />
  </svg>
);

/** Group-pill palette + hash. RESTATED from components/RedesignedMachineCard
 *  (not imported) because that file imports MUI, which the v2 stack forbids in
 *  its import graph (§3) — the same call V2_DOC_TYPE_KEYS made. Values are
 *  identical, so a machine keeps the same pill colour on both surfaces. */
const GROUP_COLORS = [
  "#3498DB", "#8E44AD", "#27AE60", "#E67E22", "#E74C3C",
  "#1ABC9C", "#2980B9", "#9B59B6", "#F39C12", "#16A085",
];

export const groupColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return GROUP_COLORS[Math.abs(hash) % GROUP_COLORS.length];
};

/** Health semantics, verbatim from the live grid: 0 = green, 1-2 = amber,
 *  3+ = red. Exported because M2.3's row SR chip paints from the SAME scale —
 *  open-service-report pressure is one signal with one colour ramp, wherever it
 *  is rendered. */
export const healthColor = (openReports: number): string =>
  openReports === 0 ? "var(--green)" : openReports <= 2 ? "var(--amber)" : "var(--red)";

/** The dot form. Painted from palette tokens so it reads on both palettes. */
export const HealthDot: React.FC<{ openReports: number }> = ({ openReports }) => {
  const { translate } = useTranslation("myMachinery");
  const color = healthColor(openReports);
  return (
    <span
      title={
        openReports === 0
          ? translate("myMachinery:healthNoIssues")
          : translate("myMachinery:healthOpenReports", { count: openReports })
      }
      style={{
        width: 10, height: 10, borderRadius: 999, background: color, flexShrink: 0,
      }}
    />
  );
};

export const NewBadge: React.FC = () => {
  const { translate } = useTranslation("myMachinery");
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700,
        letterSpacing: "0.12em", textTransform: "uppercase",
        color: "var(--mod-accent)", background: "var(--mod-accent-soft)",
        border: "1px solid var(--mod-accent-line)", borderRadius: 999,
        padding: "2px 8px", whiteSpace: "nowrap",
      }}
    >
      {translate("myMachinery:new")}
    </span>
  );
};

export interface MachinePhotoProps {
  /** Already resolved through the SHARED 4-tier fallback (useGalleryImages). */
  src: string | null;
  alt: string;
  grayscale?: boolean;
  /** Card default 92 (artboard V1BigCard); Fleet V2 rows pass 72 (V2Row).
   *  Generalised in place at M2.3 rather than forking a row-sized copy. */
  height?: number;
  /** Card/row default 120; the M2.4 profile hero passes 170 (artboard 0504
   *  draws a 170x112 hero photo). Generalised in place for the same reason
   *  `height` was — a hero-sized fork would drift from this one. */
  width?: number;
}

/** The photo well. Consumes the shared fallback's OUTPUT and paints a plain
 *  <img>; the ImageGallery component itself is MUI-based and stays out of v2. */
export const MachinePhoto: React.FC<MachinePhotoProps> = ({
  src,
  alt,
  grayscale,
  height = 92,
  width = 120,
}) => (
  <div
    style={{
      width, height, borderRadius: 8, overflow: "hidden",
      background: "var(--card-2)", border: "1px solid var(--border-soft)",
      display: "grid", placeItems: "center", color: "var(--faint)",
      filter: grayscale ? "grayscale(1)" : undefined,
    }}
  >
    {src ? (
      <img src={src} alt={alt} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    ) : (
      <CameraGlyph />
    )}
  </div>
);

export interface KVProps {
  label: string;
  value?: string | null;
  sub?: string | null;
  mono?: boolean;
}

/** One cell of the artboard's 2-column identity grid. */
export const KV: React.FC<KVProps> = ({ label, value, sub, mono }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
    <span
      style={{
        fontSize: 10, color: "var(--faint)", fontFamily: "var(--font-mono)",
        letterSpacing: "0.06em", textTransform: "uppercase",
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 12.5, color: "var(--ink)", fontWeight: 500,
        fontFamily: mono ? "var(--font-mono)" : "inherit",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}
    >
      {value || "—"}
    </span>
    {sub && (
      <span
        style={{
          fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap",
          overflow: "hidden", textOverflow: "ellipsis",
        }}
      >
        {sub}
      </span>
    )}
  </div>
);
